import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from "react";
import { FilePreviewData } from "../components/fieldMappingTable/types/fieldMappingType";
import { VariableItem } from "../components/hierarchyTree/types/hierarchyTreeTypes";

export interface JobData {
  selectedFile: File | null;
  fileExtension: string | null;
  isUploadedToS3: boolean;
  delimiter: string | null;
  textQualifier: string | null;
  hasHeader: boolean;
  filePreviewData: FilePreviewData | null;
  selectedFieldIndices: number[];
  isPreviewLoading: boolean;
  previewError: string | null;
  dropdownFieldMappings: number[];
  allVariables: VariableItem[];
  selectedVariables: VariableItem[];
  createProfile: boolean;
  selectedVariablesForProfiling: VariableItem[];
  selectedBaseForProfiling: string | null;
  unmatchedRecord: string;
  outputFileHasHeader: boolean;
  outputDelimiter: string;
  outputFileFormat: string;
  jobName: string;
}

interface JobContextType {
  jobData: JobData;
  updateJobData: (partial: Partial<JobData>) => void;
  resetJobData: () => void;
}

const defaultJobData: JobData = {
  selectedFile: null,
  fileExtension: null,
  isUploadedToS3: false,
  delimiter: null,
  textQualifier: null,
  hasHeader: false,
  filePreviewData: null,
  selectedFieldIndices: [],
  isPreviewLoading: false,
  previewError: null,
  dropdownFieldMappings: [],
  allVariables: [],
  selectedVariables: [],
  createProfile: false,
  selectedVariablesForProfiling: [],
  selectedBaseForProfiling: null,
  unmatchedRecord: "",
  outputFileHasHeader: true,
  outputDelimiter: "",
  outputFileFormat: "",
  jobName: "",
};

const JobContext = createContext<JobContextType | undefined>(undefined);

export const JobProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [jobData, setJobData] = useState<JobData>(defaultJobData);

  const updateJobData = useCallback((partial: Partial<JobData>) => {
    setJobData(prev => ({ ...prev, ...partial }));
  }, []);

  const resetJobData = () => {
    setJobData(defaultJobData);
  };

  return (
    <JobContext.Provider value={{ jobData, updateJobData, resetJobData }}>
      {children}
    </JobContext.Provider>
  );
};

export const useJobContext = () => {
  const context = useContext(JobContext);
  if (!context) {
    throw new Error("useJobContext must be used within JobProvider");
  }
  return context;
};
